import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Loader, Empty, btn } from "@/components/Page";

const STANCES = [
  { value: "aggressive", label: "Aggressive", desc: "High damage, low defense" },
  { value: "balanced", label: "Balanced", desc: "Equal offense & defense" },
  { value: "defensive", label: "Defensive", desc: "High defense, low damage" }
];

const ENEMY_CLASSES = [
  { key: "Provocator", label: "vs Provocator" },
  { key: "Dimachaerus", label: "vs Dimachaerus" },
  { key: "Retiarius", label: "vs Retiarius" },
];

const defaultClassStances = () => ({
  Provocator: "balanced",
  Dimachaerus: "balanced",
  Retiarius: "balanced",
});

export default function StancePanel({ war, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [myFighters, setMyFighters] = useState([]);
  const [stances, setStances] = useState({});
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    loadMyFighters();
  }, []);
  
  const loadMyFighters = async () => {
    try {
      const res = await api.get("/alliance-wars/my-fighters");
      setMyFighters(res.data.fighters || []);
      
      // Initialize stances from current values
      const initialStances = {};
      res.data.fighters.forEach(f => {
        if (!initialStances[f.gladiator_id]) {
          initialStances[f.gladiator_id] = {};
        }
        initialStances[f.gladiator_id][f.round] = {
          ...defaultClassStances(),
          ...(f.stances_by_class || {}),
        };
      });
      setStances(initialStances);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  
  const setStance = (fighterId, round, enemyClass, stance) => {
    setStances(prev => ({
      ...prev,
      [fighterId]: {
        ...prev[fighterId],
        [round]: {
          ...defaultClassStances(),
          ...(prev[fighterId]?.[round] || {}),
          [enemyClass]: stance,
        }
      }
    }));
  };
  
  const saveStances = async () => {
    setSaving(true);
    try {
      await api.put("/alliance-wars/stances", { stances });
      toast.success("Alliance Wars tactics saved!");
      await loadMyFighters();
      onUpdate();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) return <Loader label="Loading your selections…" />;
  
  if (myFighters.length === 0) {
    return (
      <Empty label="None of your gladiators have been selected for Alliance Wars." />
    );
  }
  
  const locked = myFighters[0]?.locked;
  const allSet = myFighters.every(f => f.stance_set);
  
  // Group by gladiator
  const fighterGroups = {};
  myFighters.forEach(f => {
    if (!fighterGroups[f.gladiator_id]) {
      fighterGroups[f.gladiator_id] = {
        name: f.name,
        rating: f.rating,
        rounds: []
      };
    }
    fighterGroups[f.gladiator_id].rounds.push(f);
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border border-amber-900/30 bg-zinc-950/50 p-5 rounded-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-cinzel text-base text-amber-300 uppercase tracking-wide">
              {locked ? "🔒 Stances Locked" : "Set Combat Stances"}
            </div>
            <div className="text-xs text-zinc-500 mt-1 font-manrope">
              Choose tactics for each class your gladiators might face in every round
            </div>
          </div>
          
          {!locked && (
            <button
              onClick={saveStances}
              disabled={saving}
              data-testid="alliance-war-save-tactics-button"
              className={btn("primary")}
            >
              {saving ? "Saving..." : "Save All Tactics"}
            </button>
          )}
        </div>
        
        {allSet && !locked && (
          <div className="mt-3 flex items-center gap-2 text-xs text-green-400 border-t border-amber-900/20 pt-3">
            <span className="font-manrope">✓ All per-class tactics configured!</span>
          </div>
        )}
        
        {!allSet && !locked && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-400/90 border-t border-amber-900/20 pt-3">
            <span className="font-manrope">⚠️ Some class tactics not set - balanced defaults will be applied at lock-in</span>
          </div>
        )}
      </div>
      
      {/* Combat Guide */}
      <div className="border border-zinc-800 bg-zinc-950/50 p-5 rounded-sm">
        <div className="font-cinzel text-sm text-amber-300 uppercase tracking-wide mb-3">
          Fighting Styles
        </div>
        
        <div className="text-sm text-zinc-300 mb-4 leading-relaxed">
          Styles form a cycle — read the class you are facing and the right stance tilts the whole fight. 
          <span className="text-amber-400 font-semibold"> Aggressive ▸ Balanced ▸ Defensive ▸ Aggressive</span>.
        </div>
        
        <div className="space-y-4">
          {/* Aggressive */}
          <div>
            <div className="font-cinzel text-sm text-amber-400 mb-1">Aggressive</div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              All-out offense. Overwhelms measured, balanced opponents — but a patient defensive fighter can turn that recklessness against you.
            </p>
          </div>
          
          {/* Balanced */}
          <div>
            <div className="font-cinzel text-sm text-amber-400 mb-1">Balanced</div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Adaptable and safe. Holds a slight edge over a turtling defender and is never hard-countered — yet a committed aggressor can seize the initiative.
            </p>
          </div>
          
          {/* Defensive */}
          <div>
            <div className="font-cinzel text-sm text-amber-400 mb-1">Defensive</div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Patient and durable. Punishes reckless attackers — but a balanced opponent can pick apart the guard.
            </p>
          </div>
        </div>
      </div>
      
      {/* Fighters */}
      <div className="space-y-4">
        {Object.entries(fighterGroups).map(([fighterId, data]) => (
          <div
            key={fighterId}
            className="border border-zinc-800 bg-zinc-950/50 p-4 rounded-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <div>
                <div className="font-cinzel text-sm text-amber-400 uppercase tracking-wide">{data.name}</div>
                <div className="text-[10px] text-zinc-400 uppercase tracking-widest font-manrope">Rating: {data.rating}</div>
              </div>
            </div>
            
            <div className="space-y-2">
              {data.rounds.map(f => {
                const currentStances = {
                  ...defaultClassStances(),
                  ...(stances[fighterId]?.[f.round] || f.stances_by_class || {}),
                };
                const day = f.round + 3;
                
                return (
                  <div
                    key={`${fighterId}-${f.round}`}
                    className="bg-zinc-900/50 border border-zinc-800 p-3"
                  >
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="text-xs text-zinc-400 font-semibold">
                          Round {f.round} <span className="text-zinc-600">(Day {day})</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">Set stance by enemy class</div>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-3">
                      {ENEMY_CLASSES.map((enemy) => {
                        const selectedStance = currentStances[enemy.key] || "balanced";
                        return (
                          <div
                            key={`${fighterId}-${f.round}-${enemy.key}`}
                            className="border border-zinc-800 bg-zinc-950/60 p-3 rounded-sm"
                            data-testid={`alliance-war-class-tactics-${fighterId}-${f.round}-${enemy.key}`}
                          >
                            <div className="text-[10px] text-zinc-400 uppercase tracking-widest mb-2">{enemy.label}</div>
                            {locked ? (
                              <div className="text-xs text-amber-400 font-cinzel" data-testid={`alliance-war-class-tactics-locked-${fighterId}-${f.round}-${enemy.key}`}>
                                {STANCES.find(s => s.value === selectedStance)?.label || selectedStance}
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {STANCES.map((stance) => (
                                  <button
                                    key={stance.value}
                                    type="button"
                                    data-testid={`alliance-war-tactic-${fighterId}-${f.round}-${enemy.key}-${stance.value}`}
                                    onClick={() => setStance(fighterId, f.round, enemy.key, stance.value)}
                                    className={`px-2 py-1 text-[10px] font-cinzel uppercase tracking-wide transition ${
                                      selectedStance === stance.value
                                        ? "bg-amber-900/30 text-amber-300 border border-amber-700/50"
                                        : "bg-zinc-800/50 text-zinc-500 border border-zinc-700 hover:bg-zinc-800"
                                    }`}
                                    title={stance.desc}
                                  >
                                    {stance.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
